import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTheme } from "../../contexts/ThemeContext";
import InspectionService from "../../services/inspectionService";
import ApiService from "../../services/api";
import { Inspection, InspectionSheet } from "../../types";
import Modal from "../common/Modal";
import { MessageSquare } from "lucide-react";

interface InspectionDetails extends Inspection {
  sheets: InspectionSheet[];
}

interface TemplateItem {
  id: number;
  template_id: number;
  section_name: string;
  item_name: string;
  display_order: number;
}

interface ItemResponse {
  item_id: number;
  response: "pass" | "fail" | "na" | "checked";
  notes?: string;
}

// Memoized item component to prevent unnecessary re-renders
const InspectionItem = React.memo<{
  item: TemplateItem;
  responseType: "pass-fail-na" | "check";
  currentResponse?: ItemResponse;
  onResponse: (
    itemId: number,
    response: "pass" | "fail" | "na" | "checked"
  ) => void;
  onUpdateNotes: (itemId: number, notes: string) => void;
  isDarkMode: boolean;
}>(
  ({
    item,
    responseType,
    currentResponse,
    onResponse,
    onUpdateNotes,
    isDarkMode,
  }) => {
    return (
      <div
        className={`border rounded-lg p-4 hover:shadow-sm transition-shadow ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {item.item_name}
              </p>
            </div>
            <div className="ml-4 flex space-x-2">
              {responseType === "pass-fail-na" ? (
                <>
                  <button
                    onClick={() => onResponse(item.id, "pass")}
                    className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                      currentResponse?.response === "pass"
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-green-500 text-green-700 hover:bg-green-50"
                    }`}
                  >
                    Pass
                  </button>
                  <button
                    onClick={() => onResponse(item.id, "fail")}
                    className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                      currentResponse?.response === "fail"
                        ? "bg-red-500 border-red-500 text-white"
                        : "border-red-500 text-red-700 hover:bg-red-50"
                    }`}
                  >
                    Fail
                  </button>
                  <button
                    onClick={() => onResponse(item.id, "na")}
                    className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                      currentResponse?.response === "na"
                        ? "bg-gray-500 border-gray-500 text-white"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    N/A
                  </button>
                </>
              ) : (
                <button
                  onClick={() =>
                    onResponse(
                      item.id,
                      currentResponse?.response === "checked"
                        ? ("" as any)
                        : "checked"
                    )
                  }
                  className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors flex items-center ${
                    currentResponse?.response === "checked"
                      ? "bg-blue-500 border-blue-500 text-white"
                      : "border-blue-500 text-blue-700 hover:bg-blue-50"
                  }`}
                >
                  {currentResponse?.response === "checked" && (
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  Check
                </button>
              )}
            </div>
          </div>

          {/* Input de comentario inline - aparece cuando hay una respuesta */}
          {currentResponse?.response && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MessageSquare
                  className={`w-4 h-4 ${
                    isDarkMode ? "text-blue-400" : "text-blue-600"
                  }`}
                />
                <label
                  className={`text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Comentario (opcional)
                </label>
              </div>
              <textarea
                value={currentResponse.notes || ""}
                onChange={(e) => onUpdateNotes(item.id, e.target.value)}
                onBlur={() => {}}
                placeholder="Add observations or details..."
                className={`w-full px-3 py-2 text-sm rounded-md border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
                rows={2}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

InspectionItem.displayName = "InspectionItem";

const EditInspection: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [inspection, setInspection] = useState<InspectionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [templateItems, setTemplateItems] = useState<
    Record<number, TemplateItem[]>
  >({});
  const [loadingItems, setLoadingItems] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [itemResponses, setItemResponses] = useState<
    Record<number, ItemResponse>
  >({});
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [loadedSheets, setLoadedSheets] = useState<Set<number>>(new Set());
  const [searchFilter, setSearchFilter] = useState("");
  const [editingDetails, setEditingDetails] = useState(false);
  const [driverName, setDriverName] = useState("");
  const [mechanicName, setMechanicName] = useState("");

  useEffect(() => {
    loadInspection();
  }, [id]);

  useEffect(() => {
    if (inspection && inspection.sheets) {
      loadTemplateItems();
    }
  }, [inspection]);

  // Load responses for active tab only once
  useEffect(() => {
    if (inspection?.sheets[activeTab]) {
      const sheetId = inspection.sheets[activeTab].id;
      if (!loadedSheets.has(sheetId)) {
        loadSheetResponses(sheetId);
        setLoadedSheets((prev) => new Set(prev).add(sheetId));
      }
    }
  }, [activeTab, inspection]);

  const loadInspection = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await InspectionService.getInspectionById(Number(id));
      console.log("Inspection data loaded:", data);
      console.log("Sheets:", data.sheets);
      setInspection(data as InspectionDetails);
      // Load driver and mechanic names
      setDriverName((data as any).driver_name || "");
      setMechanicName((data as any).mechanic_name || "");
    } catch (error) {
      console.error("Error loading inspection:", error);
      setError("Error loading inspection");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateItems = async () => {
    if (!inspection?.sheets) return;

    if (!initialLoadComplete) {
      setLoadingItems(true);
    }
    const itemsMap: Record<number, TemplateItem[]> = {};

    try {
      for (const sheet of inspection.sheets) {
        const items = await ApiService.get<TemplateItem[]>(
          `/inspections/templates/${sheet.template_id}/items`
        );
        itemsMap[sheet.template_id] = items;
      }
      setTemplateItems(itemsMap);
    } catch (error) {
      console.error("Error loading template items:", error);
    } finally {
      if (!initialLoadComplete) {
        setLoadingItems(false);
        setInitialLoadComplete(true);
      }
    }
  };

  const loadSheetResponses = async (sheetId: number) => {
    try {
      const data = await ApiService.get<{
        sheet_id: number;
        responses: Array<{
          item_id: number;
          response: "pass" | "fail" | "na" | "checked";
          notes: string;
          updated_at: string;
        }>;
      }>(`/inspections/sheets/${sheetId}/responses`);

      // Convert array of responses to Record format and merge with existing responses
      const responsesMap: Record<number, ItemResponse> = {};
      data.responses.forEach((r) => {
        responsesMap[r.item_id] = {
          item_id: r.item_id,
          response: r.response,
          notes: r.notes || "",
        };
      });
      // Merge with existing responses to preserve responses from other sheets
      setItemResponses((prev) => ({ ...prev, ...responsesMap }));
    } catch (error) {
      console.error("Error loading sheet responses:", error);
    }
  };

  const handleItemResponse = useCallback(
    async (itemId: number, response: "pass" | "fail" | "na" | "checked") => {
      const currentSheet = inspection?.sheets[activeTab];
      if (!currentSheet) return;

      const responseType = getResponseType(currentSheet.type);

      // For check-type responses, toggle behavior: if already checked, uncheck it
      if (
        responseType === "check" &&
        itemResponses[itemId]?.response === "checked"
      ) {
        // Remove the response (uncheck) - update UI immediately
        const newResponses = { ...itemResponses };
        delete newResponses[itemId];
        setItemResponses(newResponses);

        // Then update backend in background (no await to prevent blocking)
        ApiService.post<{
          success: boolean;
          sheet_status: string;
          progress: { completed: number; total: number };
        }>(`/inspections/sheets/${currentSheet.id}/items/${itemId}/response`, {
          response: "", // Empty response to delete
        })
          .then((result) => {
            // Update the sheet status in local state
            if (result.sheet_status && inspection) {
              setInspection((prev) => {
                if (!prev) return prev;
                const updatedSheets = [...prev.sheets];
                updatedSheets[activeTab] = {
                  ...updatedSheets[activeTab],
                  status: result.sheet_status as
                    | "empty"
                    | "in_progress"
                    | "completed",
                };
                return { ...prev, sheets: updatedSheets };
              });
            }
          })
          .catch((error) => {
            console.error("Error removing item response:", error);
            // Revert on error
            setItemResponses((prev) => ({
              ...prev,
              [itemId]: { item_id: itemId, response: "checked" },
            }));
          });
        return;
      }

      // Para todas las respuestas (pass, fail, na, checked), simplemente guardar
      // El textarea aparecerá automáticamente debajo
      saveItemResponse(itemId, response, itemResponses[itemId]?.notes || "");
    },
    [inspection, activeTab, itemResponses, templateItems]
  );

  // Función para actualizar notas sin cambiar la respuesta
  const handleUpdateNotes = useCallback(
    (itemId: number, notes: string) => {
      const currentResponse = itemResponses[itemId];
      if (!currentResponse) return;

      // Actualizar estado local inmediatamente
      setItemResponses((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], notes },
      }));

      // Guardar en backend
      saveItemResponse(itemId, currentResponse.response, notes);
    },
    [itemResponses, inspection, activeTab]
  );

  const saveItemResponse = async (
    itemId: number,
    response: "pass" | "fail" | "na" | "checked",
    notes: string
  ) => {
    const currentSheet = inspection?.sheets[activeTab];
    if (!currentSheet) return;

    // Update local state immediately for responsive UI (optimistic update)
    const previousResponse = itemResponses[itemId];
    setItemResponses((prev) => ({
      ...prev,
      [itemId]: { item_id: itemId, response, notes },
    }));

    // Save to backend in background (no await to prevent blocking)
    ApiService.post<{
      success: boolean;
      sheet_status: string;
      progress: { completed: number; total: number };
    }>(`/inspections/sheets/${currentSheet.id}/items/${itemId}/response`, {
      response,
      notes,
    })
      .then((result) => {
        // Update the sheet status in local state
        if (result.sheet_status && inspection) {
          setInspection((prev) => {
            if (!prev) return prev;
            const updatedSheets = [...prev.sheets];
            updatedSheets[activeTab] = {
              ...updatedSheets[activeTab],
              status: result.sheet_status as
                | "empty"
                | "in_progress"
                | "completed",
            };
            return { ...prev, sheets: updatedSheets };
          });
        }
      })
      .catch((error) => {
        console.error("Error saving item response:", error);
        // Revert to previous state on error
        if (previousResponse) {
          setItemResponses((prev) => ({
            ...prev,
            [itemId]: previousResponse,
          }));
        } else {
          setItemResponses((prev) => {
            const newState = { ...prev };
            delete newState[itemId];
            return newState;
          });
        }
      });
  };

  const handleClearAll = async () => {
    const currentSheet = inspection?.sheets[activeTab];
    if (!currentSheet) return;

    if (
      !window.confirm(
        "Are you sure you want to clear all responses from this sheet?"
      )
    ) {
      return;
    }

    try {
      await ApiService.delete(
        `/inspections/sheets/${currentSheet.id}/responses`
      );

      // Clear local state
      setItemResponses({});

      // Update sheet status to empty
      if (inspection) {
        const updatedSheets = [...inspection.sheets];
        updatedSheets[activeTab] = {
          ...updatedSheets[activeTab],
          status: "empty",
        };
        setInspection({ ...inspection, sheets: updatedSheets });
      }

      console.log(`Cleared all responses for sheet ${currentSheet.id}`);
    } catch (error) {
      console.error("Error clearing responses:", error);
      enqueueSnackbar("Error clearing responses", { variant: "error" });
    }
  };

  const handleCompleteInspection = async (comment?: string) => {
    setCompleting(true);
    try {
      // Update inspection status to completed with optional comment
      await InspectionService.updateInspection(Number(id), {
        status: "closed",
        comment: comment,
      });

      // Navigate back to list
      navigate("/inspections");
    } catch (error) {
      console.error("Error completing inspection:", error);
      enqueueSnackbar("Error completing inspection", { variant: "error" });
    } finally {
      setCompleting(false);
      setShowCompleteModal(false);
    }
  };

  const handleUpdateDetails = async () => {
    try {
      await InspectionService.updateInspection(Number(id), {
        driver_name: driverName,
        mechanic_name: mechanicName,
      });

      // Update local state
      setInspection({
        ...inspection!,
        driver_name: driverName,
        mechanic_name: mechanicName,
      } as any);

      setEditingDetails(false);
      enqueueSnackbar("Details updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating details:", error);
      enqueueSnackbar("Error updating details", { variant: "error" });
    }
  };

  const getResponseType = (templateType: string): "pass-fail-na" | "check" => {
    // PRETRIP_GENERAL usa pass/fail/na
    if (templateType === "PRETRIP_GENERAL") {
      return "pass-fail-na";
    }
    // DOT templates usan simple check
    return "check";
  };

  const getTabTitle = (type: string) => {
    switch (type) {
      case "PRETRIP_GENERAL":
        return "Pre-Trip General";
      case "TRACTOR_DOT":
        return "DOT Tractor";
      case "TRAILER_DOT":
        return "DOT Trailer";
      default:
        return type;
    }
  };

  const getTabIcon = (type: string) => {
    switch (type) {
      case "PRETRIP_GENERAL":
        return (
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        );
      case "TRACTOR_DOT":
        return (
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
            />
          </svg>
        );
      case "TRAILER_DOT":
        return (
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading inspection...</p>
        </div>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3
            className={`mt-4 text-lg font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Error
          </h3>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
            {error || "Inspection not found"}
          </p>
          <button
            onClick={() => navigate("/inspections")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  if (inspection.status === "closed") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-yellow-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <h3
            className={`mt-4 text-lg font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Inspection Closed
          </h3>
          <p
            className={`mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            This inspection is closed and cannot be edited
          </p>
          <button
            onClick={() => navigate("/inspections")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/inspections")}
                className={`mr-4 transition-colors ${
                  isDarkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1
                  className={`text-2xl font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Edit Inspection #{inspection.id}
                </h1>
                <p
                  className={`text-sm mt-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Vehicle{" "}
                  {(inspection as any).vehicle?.number
                    ? `#${(inspection as any).vehicle.number}`
                    : `ID ${inspection.vehicle_id}`}{" "}
                  • Created{" "}
                  {new Date(inspection.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                Open
              </span>
            </div>
          </div>

          {/* Driver and Mechanic Details Card */}
          <div
            className={`mt-4 rounded-lg shadow p-4 ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Inspection Details
              </h3>
              {!editingDetails && (
                <button
                  onClick={() => setEditingDetails(true)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Edit
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {editingDetails ? (
                <>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Driver Name
                    </label>
                    <input
                      type="text"
                      value={driverName}
                      onChange={(e) => setDriverName(e.target.value)}
                      placeholder="Enter driver name"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Mechanic Name
                    </label>
                    <input
                      type="text"
                      value={mechanicName}
                      onChange={(e) => setMechanicName(e.target.value)}
                      placeholder="Enter mechanic name"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-end space-x-2">
                    <button
                      onClick={() => {
                        setEditingDetails(false);
                        setDriverName((inspection as any).driver_name || "");
                        setMechanicName(
                          (inspection as any).mechanic_name || ""
                        );
                      }}
                      className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateDetails}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Driver Name
                    </label>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {(inspection as any).driver_name || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Mechanic Name
                    </label>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {(inspection as any).mechanic_name || "Not specified"}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div
          className={`rounded-lg shadow ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          {/* Debug info */}
          {!inspection.sheets || inspection.sheets.length === 0 ? (
            <div className="p-6">
              <div
                className={`border rounded-md p-4 ${
                  isDarkMode
                    ? "bg-red-900/30 border-red-800 text-red-200"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                <p className="text-sm text-red-800">
                  No sheets found. Sheets data:{" "}
                  {JSON.stringify(inspection.sheets)}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Tab Headers */}
              <div
                className={`border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <nav className="flex -mb-px">
                  {inspection.sheets.map((sheet, index) => (
                    <button
                      key={sheet.id}
                      onClick={() => setActiveTab(index)}
                      className={`
                    flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors
                    ${
                      activeTab === index
                        ? "border-blue-500 text-blue-600"
                        : isDarkMode
                        ? "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                    >
                      {getTabIcon(sheet.type)}
                      <span>{getTabTitle(sheet.type)}</span>
                      <span
                        className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                          sheet.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : sheet.status === "in_progress"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {sheet.status}
                      </span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {inspection.sheets && inspection.sheets[activeTab] && (
                  <div>
                    <div className="mb-6 flex items-start justify-between">
                      <div>
                        <h2
                          className={`text-xl font-semibold ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {inspection.sheets[activeTab].template_name}
                        </h2>
                        <p
                          className={`mt-1 text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {getResponseType(
                            inspection.sheets[activeTab].type
                          ) === "pass-fail-na"
                            ? "Complete the checklist items below. Mark each item as Pass, Fail, or N/A."
                            : "Check the items that have been inspected."}
                        </p>
                      </div>

                      {/* Clear All button - only for PRETRIP */}
                      {getResponseType(inspection.sheets[activeTab].type) ===
                        "pass-fail-na" && (
                        <button
                          onClick={handleClearAll}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm font-medium"
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    {/* Search Filter */}
                    <div className="mb-4">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg
                            className="h-5 w-5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Search items by name or section..."
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          className={`block w-full pl-10 pr-10 py-2 border rounded-md leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {searchFilter && (
                          <button
                            onClick={() => setSearchFilter("")}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            <svg
                              className="h-5 w-5 text-gray-400 hover:text-gray-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {loadingItems ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <>
                        {templateItems[
                          inspection.sheets[activeTab].template_id
                        ] ? (
                          <div className="space-y-3">
                            {/* Group items by section and apply filter */}
                            {(() => {
                              const filteredSections = Object.entries(
                                templateItems[
                                  inspection.sheets[activeTab].template_id
                                ].reduce((acc, item) => {
                                  // Apply search filter
                                  const searchTerm = searchFilter.toLowerCase();
                                  const matchesSearch =
                                    searchTerm === "" ||
                                    item.item_name
                                      .toLowerCase()
                                      .includes(searchTerm) ||
                                    item.section_name
                                      .toLowerCase()
                                      .includes(searchTerm);

                                  if (matchesSearch) {
                                    if (!acc[item.section_name]) {
                                      acc[item.section_name] = [];
                                    }
                                    acc[item.section_name].push(item);
                                  }
                                  return acc;
                                }, {} as Record<string, TemplateItem[]>)
                              );

                              if (
                                filteredSections.length === 0 &&
                                searchFilter
                              ) {
                                return (
                                  <div
                                    className={`border rounded-md p-6 text-center ${
                                      isDarkMode
                                        ? "bg-yellow-900/30 border-yellow-800"
                                        : "bg-yellow-50 border-yellow-200"
                                    }`}
                                  >
                                    <svg
                                      className="mx-auto h-12 w-12 text-yellow-400 mb-3"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                      />
                                    </svg>
                                    <p
                                      className={`text-sm ${
                                        isDarkMode
                                          ? "text-yellow-200"
                                          : "text-yellow-800"
                                      }`}
                                    >
                                      No items match your search:{" "}
                                      <strong>"{searchFilter}"</strong>
                                    </p>
                                    <button
                                      onClick={() => setSearchFilter("")}
                                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                                    >
                                      Clear search
                                    </button>
                                  </div>
                                );
                              }

                              return filteredSections.map(
                                ([sectionName, items]) => (
                                  <div key={sectionName} className="mb-6">
                                    <h3
                                      className={`text-lg font-semibold mb-3 pb-2 border-b ${
                                        isDarkMode
                                          ? "text-gray-200 border-gray-700"
                                          : "text-gray-800 border-gray-200"
                                      }`}
                                    >
                                      {sectionName}
                                      <span
                                        className={`ml-2 text-sm font-normal ${
                                          isDarkMode
                                            ? "text-gray-400"
                                            : "text-gray-500"
                                        }`}
                                      >
                                        ({items.length}{" "}
                                        {items.length === 1 ? "item" : "items"})
                                      </span>
                                    </h3>
                                    <div className="space-y-2">
                                      {items.map((item) => (
                                        <InspectionItem
                                          key={item.id}
                                          item={item}
                                          responseType={getResponseType(
                                            inspection.sheets[activeTab].type
                                          )}
                                          currentResponse={
                                            itemResponses[item.id]
                                          }
                                          onResponse={handleItemResponse}
                                          onUpdateNotes={handleUpdateNotes}
                                          isDarkMode={isDarkMode}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                            <p className="text-sm text-yellow-800">
                              No checklist items found for this template.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div
                className={`border-t px-6 py-4 flex items-center justify-between ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => navigate("/inspections")}
                  className={`px-4 py-2 border rounded-md transition-colors ${
                    isDarkMode
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowCompleteModal(true)}
                  disabled={completing}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {completing ? "Completing..." : "Complete Inspection"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Complete Inspection Modal */}
      <Modal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        onConfirm={handleCompleteInspection}
        title="Complete Inspection"
        message="Are you sure you want to complete this inspection? Once completed, it cannot be edited again."
        confirmText="Complete"
        cancelText="Cancel"
        type="success"
        showCommentField={true}
        commentPlaceholder="Add any final comments or notes about this inspection..."
      />
    </div>
  );
};

export default EditInspection;
